import {defaults, assign} from "lodash";
import {FormGroup} from "@angular/forms";
import {QuickFormArray} from "./quick-form-group";
import {JSONFormGroup} from "../json-shared.model";
import {JSONModel} from "../json.model";

export interface FormListOptions<T> {
  items?: T[];
  formKeys?: string[];
  indexField?: string;
}

function getFormListDefaults<T>(): FormListOptions<T> {
  return {
    items: [],
    formKeys: [],
    indexField: undefined,
  };
}

export class FormList<T> extends JSONModel implements JSONFormGroup {
  listItems: T[] = [];
  form: FormGroup;
  indexField: string = undefined;
  hasAnyItems: boolean = false;

  get items(): T[] {
    return this.listItems;
  }

  set items(items: T[]) {
    this.listItems = items;
    this.updateFormGroup();
    this.hasAnyItems = items.length > 0;
  }

  constructor(private readonly options: FormListOptions<T> = {}) {
    super();

    this.options = assign({}, this.options);
    this.options = defaults(this.options, getFormListDefaults());

    this.items = this.options.items;
    this.indexField = this.options.indexField;
  }

  private updateFormGroup() {
    if (this.options.formKeys && this.options.formKeys.length) {
      this.form = QuickFormArray.buildGroup(this.items, this.options.formKeys);
    }
  }

  forEachItem(delegate: (formGroup: FormGroup, item: T, index: number) => void) {
    const groups = this.form.controls["items"]["controls"] as FormGroup[];

    for (let index = 0; index < this.items.length; index++) {
      const item = this.items[index];
      const formGroup = groups[index];

      delegate(formGroup, item, index);
    }
  }

  private updateIndexes() {
    if (!this.indexField) return;

    this.listItems.forEach((item, index) => {
      item[this.indexField] = index as any;
    });

    const groups = this.form.controls["items"]["controls"] as FormGroup[];

    groups.forEach((item, index) => {
      if (item.controls[this.indexField]) {
        item.controls[this.indexField].setValue(index as any);
      }
    });
  }

  insertForAdd(item?: T): T {
    this.listItems.splice(0, 0, item);

    if (this.indexField) {
      item[this.indexField] = (-0.1) as any;
    }

    const formArray = this.form.controls["items"] as QuickFormArray;
    const formGroup = formArray.buildNewFormGroup(item);
    formArray.insert(0, formGroup);
    formGroup.markAsDirty();

    this.updateIndexes();

    return item;
  }

  remove(item?: any) {
    const index = item ? this.listItems.indexOf(item) : -1;

    if (index < 0 || index >= this.listItems.length) return;

    this.listItems.splice(index, 1);

    const fa = this.form.controls["items"] as QuickFormArray;
    fa.removeAt(index);
    fa.markAsDirty();

    this.updateIndexes();
  }

  move(item: any, direction: number) {
    if (direction === 0) return;

    const thisIndex = this.listItems.indexOf(item);
    const otherIndex = thisIndex + direction;

    if (thisIndex < 1) return;
    if (otherIndex < 0 || otherIndex >= this.listItems.length) return;

    [this.listItems[otherIndex], this.listItems[thisIndex]] = [this.listItems[thisIndex], this.listItems[otherIndex]];

    const formArray: QuickFormArray = this.form.controls["items"] as QuickFormArray;
    const formGroup = formArray.controls[thisIndex];

    formGroup.markAsDirty();

    formArray.removeAt(thisIndex);
    formArray.insert(otherIndex, formGroup);

    this.updateIndexes();
  }
}
